import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import styles from "../../assets/styles/YogaCourse/Yinyoga.module.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API_URL = `${API_BASE}/api/yin-yoga`;

const DEFAULT_CONTENT = {
  heroEyebrow: "50-Hour Certified Training",
  heroTitle: "The Art of <em>Yin Yoga</em><br />Still. Deep. Transformative.",
  heroSubtitle:
    "A slow, meditative journey into the deep connective tissues of the body — bridging traditional yogic wisdom with modern anatomical understanding.",
  heroImage:
    "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1800&q=85",
  heroBadges: ["50 Hours", "50+ Poses", "All Levels", "Certificate"],
  stats: [
    { num: "50+", label: "Yoga Poses" },
    { num: "6", label: "Core Modules" },
    { num: "12", label: "Meridians Covered" },
    { num: "∞", label: "Lifetime Access" },
  ],
  modules: [
    {
      img: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&q=80",
      title: "Yin Pranayama",
      desc: "Master breath as a gateway to deep stillness and nervous-system regulation.",
      tags: ["Breathwork", "Mudras", "Heating", "Cooling"],
    },
    {
      img: "https://images.unsplash.com/photo-1545389336-cf090694435e?w=600&q=80",
      title: "Yin Asanas",
      desc: "50+ poses that gently stretch connective tissues, joints, and fascia with safe alignment.",
      tags: ["Alignment", "Props", "Transitions", "Safety"],
    },
    {
      img: "https://images.unsplash.com/photo-1616699002805-0741e1e4a9c5?w=600&q=80",
      title: "Anatomy & Fascia",
      desc: "Deep understanding of joints, connective tissue, and the emotional body through the lens of fascia.",
      tags: ["Fascia", "Joints", "Compression", "Tension"],
    },
    {
      img: "https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=600&q=80",
      title: "Yin Philosophy",
      desc: "Explore Taoist wisdom, meridian theory, chakras, nadis, and the history of Yin Yoga.",
      tags: ["Taoism", "Meridians", "Chakras", "Ayurveda"],
    },
    {
      img: "https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?w=600&q=80",
      title: "Meditation",
      desc: "Build a sustainable inner practice through breath awareness, chakra meditation, and concentration.",
      tags: ["Mindfulness", "Relaxation", "Breath", "Chakra"],
    },
    {
      img: "https://images.unsplash.com/photo-1588286840104-8957b019727f?w=600&q=80",
      title: "Teaching Methodology",
      desc: "Develop professional skills to guide, sequence, and manage powerful Yin Yoga classes.",
      tags: ["Communication", "Sequencing", "Props", "Class Mgmt"],
    },
  ],
  poses: [
    "Melting Heart",
    "Banana Pose",
    "Butterfly",
    "Half Butterfly",
    "Camel Pose",
    "Caterpillar",
    "Child's Pose",
    "Dragon Pose",
    "Frog Pose",
    "Happy Baby",
    "Spinal Twist",
    "Saddle Pose",
    "Shoelace",
    "Snail Pose",
    "Sphinx & Seal",
    "Swan & Sleeping Swan",
    "Square Pose",
    "Squat Pose",
    "Toe Squat",
    "Straddle Pose",
    "Shavasana",
  ],
  philosophy: [
    {
      img: "https://images.unsplash.com/photo-1552196563-55cd4e45efb3?w=500&q=80",
      title: "Yin & Yang",
      text: "Balance opposing forces—stillness and motion, effort and ease—as the foundation of practice.",
    },
    {
      img: "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=500&q=80",
      title: "Taoist Roots",
      text: "Yin Yoga draws from Tao philosophy, teaching harmony with natural rhythms and inner silence.",
    },
    {
      img: "https://images.unsplash.com/photo-1574269909862-7e1d70bb8078?w=500&q=80",
      title: "12 Meridians",
      text: "Energy pathways mapped in Traditional Chinese Medicine, stimulated through long-held postures.",
    },
    {
      img: "https://images.unsplash.com/photo-1600618528240-fb9fc964b853?w=500&q=80",
      title: "7 Chakras",
      text: "Energetic centers of the subtle body, balanced and awakened through targeted Yin practice.",
    },
    {
      img: "https://images.unsplash.com/photo-1588928173566-e32fc4e55b8e?w=500&q=80",
      title: "5 Pranas",
      text: "The vital life forces governing movement, digestion, elimination, and higher consciousness.",
    },
    {
      img: "https://images.unsplash.com/photo-1559595500-e15296bdbb48?w=500&q=80",
      title: "Ayurvedic View",
      text: "Understand your constitutional type and how Yin Yoga supports balance across mind-body doshas.",
    },
  ],
  anatomyPoints: [
    {
      strong: "Connective Tissue & Fascia",
      text: " — understand how slow, sustained holds reshape deep tissues beyond muscle reach.",
    },
    {
      strong: "Joint Complexes",
      text: " — map the shoulder, hip, spine, and knee for safe, effective sequencing.",
    },
    {
      strong: "Compression vs Tension",
      text: " — learn to distinguish bony limits from soft-tissue stretch and teach accordingly.",
    },
    {
      strong: "Pelvis as Emotional Center",
      text: " — explore the psycho-somatic relationship stored in the pelvic bowl.",
    },
    {
      strong: "Trauma Release",
      text: " — discover how Yin postures can gently facilitate the release of held tension.",
    },
  ],
  whoCanJoin: [
    {
      img: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&q=80",
      label: "Beginners",
    },
    {
      img: "https://images.unsplash.com/photo-1575052814086-f385e2e2ad1b?w=400&q=80",
      label: "Practitioners",
    },
    {
      img: "https://images.unsplash.com/photo-1524863479829-916d8e77f114?w=400&q=80",
      label: "Yoga Teachers",
    },
    {
      img: "https://images.unsplash.com/photo-1602192509154-0b900ee1f851?w=400&q=80",
      label: "Wellness Seekers",
    },
    {
      img: "https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=400&q=80",
      label: "Healing Enthusiasts",
    },
    {
      img: "https://images.unsplash.com/photo-1603988363607-e1e4a66962c6?w=400&q=80",
      label: "Meditators",
    },
  ],
  cta: {
    title: "Begin Your Journey into<br />Deep Stillness",
    description:
      "Join our 50-Hour Yin Yoga Training and gain the knowledge, confidence, and practice to transform yourself — and the students you teach.",
    buttonText: "Enroll Now →",
    buttonLink: "#",
    image:
      "https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=1800&q=80",
  },
  aboutSectionLabel: "What You'll Learn",
  aboutSectionTitle: "Course Modules",
  aboutSectionDescription:
    "A complete, deeply layered curriculum spanning breathwork, philosophy, anatomy, postures, meditation, and professional teaching skills.",
  philosophySectionLabel: "Ancient Wisdom",
  philosophySectionTitle: "Yin Yoga Philosophy",
  philosophySectionDescription:
    "Rooted in Taoist philosophy and yogic tradition, Yin Yoga offers a rich map of the energetic body and our relationship with the universe.",
  anatomySectionLabel: "Science of Stillness",
  anatomySectionTitle: "Understanding the Body in Yin",
  anatomyIntro:
    "Yin Yoga works far below the muscular layer. This module builds a thorough anatomical vocabulary so you can practice and teach with precision and care.",
  whoSectionLabel: "Open to All",
  whoSectionTitle: "Who Can Join?",
  whoSectionDescription:
    "No prior experience is required. This training welcomes anyone drawn to the healing, stillness, and depth that Yin Yoga offers.",
  ctaSectionLabel: "Start Today",
  ctaSectionTitle: "Begin Your Journey into<br />Deep Stillness",
  ctaDescription:
    "Join our 50-Hour Yin Yoga Training and gain the knowledge, confidence, and practice to transform yourself — and the students you teach.",
};

const resolveImageSrc = (value) => {
  if (!value || typeof value !== "string") return "";
  if (/^https?:\/\//.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${API_BASE}${value}`;
  return `${API_BASE}/uploads/${value}`;
};

const normalizeTextArray = (value, fallback) =>
  Array.isArray(value) && value.length ? value.filter(Boolean) : fallback;

const normalizeObjectArray = (value, fallback) =>
  Array.isArray(value) && value.length ? value : fallback;

const mergeContent = (incoming) => ({
  ...DEFAULT_CONTENT,
  ...incoming,
  heroBadges: normalizeTextArray(incoming?.heroBadges, DEFAULT_CONTENT.heroBadges),
  stats: normalizeObjectArray(incoming?.stats, DEFAULT_CONTENT.stats),
  modules: normalizeObjectArray(incoming?.modules, DEFAULT_CONTENT.modules),
  poses: normalizeTextArray(incoming?.poses, DEFAULT_CONTENT.poses),
  philosophy: normalizeObjectArray(incoming?.philosophy, DEFAULT_CONTENT.philosophy),
  anatomyPoints: normalizeObjectArray(
    incoming?.anatomyPoints,
    DEFAULT_CONTENT.anatomyPoints
  ),
  whoCanJoin: normalizeObjectArray(incoming?.whoCanJoin, DEFAULT_CONTENT.whoCanJoin),
  cta: {
    ...DEFAULT_CONTENT.cta,
    ...(incoming?.cta || {}),
  },
});

const HtmlText = ({ as: Tag = "div", className, html }) => (
  <Tag className={className} dangerouslySetInnerHTML={{ __html: html || "" }} />
);

export default function YinYogaPage() {
  const navigate = useNavigate();
  const [content, setContent] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(API_URL);
        const latest = res.data?.current || (Array.isArray(res.data?.data) ? res.data.data[0] : null);
        if (latest) {
          setContent(mergeContent(latest));
        }
      } catch (error) {
        console.error("Yin Yoga fetch error:", error.response?.data || error.message);
      }
    };

    fetchContent();
  }, []);

  const goToLink = (link) => {
    if (!link) return;
    if (link.startsWith("#")) {
      window.location.hash = link;
      return;
    }
    if (/^https?:\/\//.test(link)) {
      window.location.href = link;
      return;
    }
    navigate(link);
  };

  return (
    <div className={styles.pageWrapper}>
      <section className={styles.hero}>
        <div className={styles.heroImageOverlay} />
        <img
          src={resolveImageSrc(content.heroImage)}
          alt="Yin Yoga"
          className={styles.heroBg}
        />
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <div className="row align-items-center" style={{ minHeight: "88vh" }}>
            <div className="col-lg-7">
              <span className={styles.heroEyebrow}>{content.heroEyebrow}</span>
              <HtmlText
                as="h1"
                className={styles.heroTitle}
                html={content.heroTitle}
              />
              <p className={styles.heroSubtitle}>{content.heroSubtitle}</p>
              <div className={styles.heroBadgeRow}>
                {content.heroBadges.map((badge) => (
                  <span className={styles.heroBadge} key={badge}>
                    {badge}
                  </span>
                ))}
              </div>
              <div className="d-flex flex-wrap gap-3 mt-4">
                <a href="#enroll" className={styles.btnPrimary}>
                  Enroll Now
                </a>
                <a href="#modules" className={styles.btnOutline}>
                  Explore Course
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.heroScrollHint}>
          <div className={styles.scrollLine} />
          <span>Scroll</span>
        </div>
      </section>

      <section className={styles.statsSection}>
        <div className="container">
          <div className={styles.statsRow}>
            {content.stats.map((stat, index) => (
              <React.Fragment key={`${stat.label}-${index}`}>
                <div className={styles.statItem}>
                  <span className={styles.statNumber}>{stat.num}</span>
                  <span className={styles.statLabel}>{stat.label}</span>
                </div>
                {index < content.stats.length - 1 && <div className={styles.statDivider} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section id="modules" className={styles.section}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEyebrow}>{content.aboutSectionLabel}</span>
            <h2 className={styles.sectionTitle}>{content.aboutSectionTitle}</h2>
            <div className={styles.sectionDivider} />
            <p className={styles.sectionDescription}>{content.aboutSectionDescription}</p>
          </div>
          <div className="row g-4">
            {content.modules.map((moduleItem, index) => (
              <div className="col-md-6 col-lg-4" key={`${moduleItem.title}-${index}`}>
                <div className={styles.moduleCard}>
                  <div className={styles.moduleCardImageWrap}>
                    <img
                      src={resolveImageSrc(moduleItem.img)}
                      alt={moduleItem.title}
                      className={styles.moduleCardImage}
                    />
                    <div className={styles.moduleCardOverlay} />
                  </div>
                  <div className={styles.moduleCardBody}>
                    <h3 className={styles.moduleCardTitle}>{moduleItem.title}</h3>
                    <p className={styles.moduleCardDesc}>{moduleItem.desc}</p>
                    <ul className={styles.moduleTagList}>
                      {(moduleItem.tags || []).map((tag) => (
                        <li className={styles.moduleTag} key={tag}>
                          {tag}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="poses" className={styles.posesSection}>
        <div className={styles.posesBgImage}>
          <img
            src="https://images.unsplash.com/photo-1545389336-cf090694435e?w=1600&q=80"
            alt="poses bg"
            className={styles.posesBg}
          />
          <div className={styles.posesOverlay} />
        </div>
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEyebrowLight}>Practice</span>
            <h2 className={styles.sectionTitleLight}>Yin Yoga Asanas</h2>
            <div className={styles.sectionDividerLight} />
            <p className={styles.sectionDescriptionLight}>
              Practice {content.poses.length}+ Yin postures that gently melt tension
              from the deepest layers of the body.
            </p>
          </div>
          <div className={styles.poseGrid}>
            {content.poses.map((pose) => (
              <div className={styles.poseChip} key={pose}>
                {pose}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="philosophy" className={styles.philosophySection}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEyebrow}>{content.philosophySectionLabel}</span>
            <h2 className={styles.sectionTitle}>{content.philosophySectionTitle}</h2>
            <div className={styles.sectionDivider} />
            <p className={styles.sectionDescription}>
              {content.philosophySectionDescription}
            </p>
          </div>
          <div className="row g-4">
            {content.philosophy.map((item, index) => (
              <div className="col-sm-6 col-lg-4" key={`${item.title}-${index}`}>
                <div className={styles.philCard}>
                  <div className={styles.philCardImageWrap}>
                    <img
                      src={resolveImageSrc(item.img)}
                      alt={item.title}
                      className={styles.philCardImage}
                    />
                    <div className={styles.philCardGradient} />
                    <h4 className={styles.philCardTitle}>{item.title}</h4>
                  </div>
                  <div className={styles.philCardBody}>
                    <p className={styles.philCardText}>{item.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="anatomy" className={styles.anatomySection}>
        <div className="container">
          <div className="row align-items-center gy-5">
            <div className="col-lg-5">
              <div className={styles.anatomyImageWrap}>
                <img
                  src={resolveImageSrc(content.modules[2]?.img || content.heroImage)}
                  alt="Anatomy"
                  className={styles.anatomyImage}
                />
                <div className={styles.anatomyImageAccent} />
                <div className={styles.anatomyBadge}>
                  <span className={styles.anatomyBadgeNum}>
                    {String(content.anatomyPoints.length || 0)}
                  </span>
                  <span className={styles.anatomyBadgeText}>
                    Key Anatomical
                    <br />
                    Focus Areas
                  </span>
                </div>
              </div>
            </div>
            <div className="col-lg-6 offset-lg-1">
              <span className={styles.sectionEyebrow}>{content.anatomySectionLabel}</span>
              <h2 className={styles.sectionTitle}>{content.anatomySectionTitle}</h2>
              <div className={styles.sectionDivider} style={{ margin: "12px 0 24px" }} />
              <p className={styles.anatomyIntro}>{content.anatomyIntro}</p>
              <ul className={styles.anatomyFeatureList}>
                {content.anatomyPoints.map((point, index) => (
                  <li className={styles.anatomyFeatureItem} key={`${point.strong}-${index}`}>
                    <div className={styles.anatomyFeatureNum}>
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <strong className={styles.anatomyFeatureStrong}>
                        {point.strong}
                      </strong>
                      <span className={styles.anatomyFeatureText}>{point.text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="who" className={styles.whoSection}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionEyebrow}>{content.whoSectionLabel}</span>
            <h2 className={styles.sectionTitle}>{content.whoSectionTitle}</h2>
            <div className={styles.sectionDivider} />
            <p className={styles.sectionDescription}>{content.whoSectionDescription}</p>
          </div>
          <div className="row g-4 justify-content-center">
            {content.whoCanJoin.map((item, index) => (
              <div className="col-6 col-md-4 col-lg-2" key={`${item.label}-${index}`}>
                <div className={styles.whoCard}>
                  <div className={styles.whoCardImageWrap}>
                    <img
                      src={resolveImageSrc(item.img)}
                      alt={item.label}
                      className={styles.whoCardImage}
                    />
                    <div className={styles.whoCardOverlay} />
                  </div>
                  <div className={styles.whoCardLabel}>{item.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="enroll" className={styles.ctaBanner}>
        <img
          src={resolveImageSrc(content.cta.image)}
          alt={content.cta.buttonText || "Enroll"}
          className={styles.ctaBannerBg}
        />
        <div className={styles.ctaBannerOverlay} />
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-7">
              <span className={styles.sectionEyebrowLight}>{content.ctaSectionLabel}</span>
              <HtmlText
                as="h2"
                className={styles.ctaBannerTitle}
                html={content.ctaSectionTitle || content.cta.title}
              />
              <p className={styles.ctaBannerDesc}>
                {content.ctaDescription || content.cta.description}
              </p>
              <button
                type="button"
                className={styles.btnWhite}
                onClick={() => goToLink(content.cta.buttonLink)}
              >
                {content.cta.buttonText}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
